import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Cite from "citation-js";
import {
  Box,
  Button,
  Container,
  IconButton,
  TextField,
  useTheme,
} from "@mui/material";
import SourceTypeSelect from "./SourceTypeSelect";
import StyleSelect from "./StyleSelect";
import AuthorForm from "./AuthorForm";
import debounce from "lodash.debounce";
import inputConfig from "./inputData";
import { CopyAllOutlined } from "@mui/icons-material";
import CitationTabs from "./CitationTabs";
import {
  addCustomStyles,
  initializeCitationData,
  resetCitationDataValues,
  copyToClipboard,
} from "./helpers";
import BookSearch from "./bookSearch";
import BookTabsMode from "./BookTabsMode";

function Citation() {
  const [citationTab, setCitationTab] = useState("fullCitation");
  const [selectedStyle, setSelectedStyle] = useState("apa");
  const [selectedSourceType, setSelectedSourceType] = useState("book");

  const previousCitationTabRef = useRef(citationTab);
  const previousSelectedStyleRef = useRef(selectedStyle);
  const previousSourceTypeRef = useRef(selectedSourceType);

  // render field inputs based on style - source - tab
  const fields = useMemo(() => {
    const citationConfig =
      citationTab === "textCitation"
        ? inputConfig.textCitation?.[selectedSourceType]
        : inputConfig[citationTab]?.[selectedStyle]?.[selectedSourceType];

    return citationConfig || { required: [], optional: [] };
  }, [selectedStyle, selectedSourceType, citationTab]);
  // data
  const [citationData, setCitationData] = useState(() => {
    return initializeCitationData(fields);
  });
  const [authors, setAuthors] = useState([{ given: "", family: "" }]);

  const [bookActiveStep, setBookActiveStep] = useState(0);
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookTab, setBookTab] = useState("search");

  // result
  const [generatedCitation, setGeneratedCitation] = useState("");
  const [citations, setCitations] = useState([]);

  const theme = useTheme();

  useEffect(() => {
    const citationTabHasChanged =
      citationTab !== previousCitationTabRef.current;
    const styleHasChanged = selectedStyle !== previousSelectedStyleRef.current;
    const sourceTypeHasChanged =
      selectedSourceType !== previousSourceTypeRef.current;

    let newCitationData;
    switch (true) {
      case sourceTypeHasChanged:
        newCitationData = initializeCitationData(fields);
        break;
      case citationTabHasChanged:
        newCitationData = initializeCitationData(fields, citationData, true);
        break;
      case styleHasChanged:
        newCitationData = initializeCitationData(fields, citationData, true);

        break;
      default:
        newCitationData = citationData;
    }
    newCitationData.type = selectedSourceType || "";
    setCitationData(newCitationData);

    // Update refs after changes
    previousCitationTabRef.current = citationTab;
    previousSelectedStyleRef.current = selectedStyle;
    previousSourceTypeRef.current = selectedSourceType;
  }, [selectedStyle, selectedSourceType, citationTab, fields]);

  useEffect(() => {
    // Update the 'authors' field in CitationData when authors change
    setCitationData((prevCitationData) => ({
      ...prevCitationData,
      author: authors,
    }));
  }, [authors, selectedStyle, selectedSourceType, citationTab]);

  const handleSourceTypeChange = (e) => {
    const newSourceType = e.target.value;
    setSelectedSourceType(newSourceType);
    // Initialize citationData with type property
    setCitationData({
      type: newSourceType,
    });
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    if (name && value !== undefined) {
      const updatedValue =
        name === "issued" || name === "accessed"
          ? { "date-parts": [[value || ""]] }
          : value || "";

      setCitationData((prevCitationData) => ({
        ...prevCitationData,
        [name]: updatedValue,
      }));
    }
  };

  const handleCitationTabChange = (event, newValue) => {
    setCitationTab(newValue);
  };
  const handleBookTabChange = (event, newValue) => {
    setBookTab(newValue);
    setBookActiveStep(0);
    setSelectedBook(null);
    setCitationData(() => {
      return initializeCitationData(fields);
    });
  };
  useEffect(() => {
    addCustomStyles();
  }, []);
  const generateCitation = useCallback(() => {
    if (citationData) {
      try {
        const citation = new Cite(citationData);
        const citationFormat =
          citationTab == "fullCitation" ? "bibliography" : "citation";
        const formattedCitation = citation.format(citationFormat, {
          format: "text",
          template:
            citationTab == "fullCitation" ? selectedStyle || "apa" : null,
          lang: "en-US",
        });
        setGeneratedCitation(formattedCitation);
      } catch (error) {
        console.error("Failed to format the citation:", error);
        setGeneratedCitation(
          "An error occurred while generating the citation."
        );
      }
    } else {
      // Reset the citation if no values are provided.
      setGeneratedCitation("");
    }
  }, [citationData, selectedSourceType, selectedStyle, citationTab]);

  const debouncedGenerateCitation = useCallback(
    debounce(generateCitation, 500),
    [generateCitation]
  );

  useEffect(() => {
    // Trigger the debounced function on data change
    debouncedGenerateCitation();

    // Cleanup function to cancel the debounce if the component is unmounted or the dependencies change
    return () => {
      debouncedGenerateCitation.cancel();
    };
  }, [debouncedGenerateCitation, citationData, selectedStyle]);

  // render inputs based on style - source - tab
  const citationInputs = useMemo(() => {
    const requiredFields = fields.required || [];
    const optionalFields = fields.optional || [];

    return [
      ...requiredFields.map((field) => ({ name: field, isRequired: true })),
      ...optionalFields?.map((field) => ({ name: field, isRequired: false })),
    ];
  }, [selectedStyle, selectedSourceType, citationTab, fields]);

  // console.log("generatedCitation:", generatedCitation);
  console.log("citationData:", citationData);

  const boxBorderStyle = {
    border: `1px solid ${theme.palette.divider}`,
    p: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  };

  const flexibleContainerStyle = {
    display: "flex",
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
  };

  const previewBoxStyle = {
    border: `1px solid ${theme.palette.divider}`,
    p: theme.spacing(2),
    height: "fit-content",
  };

  return (
    <Container sx={{ ...flexibleContainerStyle, p: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          ...flexibleContainerStyle,
          gap: 4,
          flexDirection: { xs: "column", md: "row" },
        }}>
        <Box
          sx={{
            ...boxBorderStyle,
            width: { xs: "100%", md: "70%" },
            gap: { xs: 2, md: 3 },
          }}>
          <>
            <h2>Citation Information</h2>
            <StyleSelect
              selectedStyle={selectedStyle}
              setSelectedStyle={setSelectedStyle}
              disabled={citationTab == "textCitation"}
            />
          </>

          <SourceTypeSelect
            selectedSourceType={selectedSourceType}
            handleSourceTypeChange={handleSourceTypeChange}
          />
          {/* tabs and on tab change reset search stuff and citation data */}
          {selectedSourceType === "book" ? (
            <BookTabsMode
              bookTab={bookTab}
              handleBookTabChange={handleBookTabChange}
              searchContent={
                <>
                  <BookSearch
                    activeStep={bookActiveStep}
                    setActiveStep={setBookActiveStep}
                    citationData={citationData}
                    setCitationData={setCitationData}
                    setAuthors={setAuthors}
                    selectedStyle={selectedStyle}
                    citationTab={citationTab}
                    selectedBook={selectedBook}
                    setSelectedBook={setSelectedBook}
                    handleBookTabChange={handleBookTabChange}
                  />

                  {bookActiveStep === 2 && (
                    <>
                      <Box>
                        <Button
                          onClick={() => {
                            setSelectedBook(null);
                            setBookActiveStep(0);
                          }}>
                          Reset
                        </Button>
                      </Box>

                      {/* step 3 */}
                      <AuthorForm authors={authors} setAuthors={setAuthors} />
                      <CitationTabs
                        citationTab={citationTab}
                        handleCitationTabChange={handleCitationTabChange}
                        citationInputs={
                          <>
                            {citationInputs?.map(({ name, isRequired }) => (
                              <TextField
                                key={name}
                                id={name}
                                name={name}
                                label={
                                  name.charAt(0).toUpperCase() + name.slice(1)
                                }
                                value={
                                  name === "issued"
                                    ? citationData["issued"]?.[
                                        "date-parts"
                                      ]?.[0]?.[0] || ""
                                    : name === "accessed"
                                    ? citationData["accessed"]?.[
                                        "date-parts"
                                      ]?.[0]?.[0] || ""
                                    : citationData[name] || ""
                                }
                                onChange={handleFieldChange}
                                fullWidth
                                required={isRequired}
                                helperText={
                                  name === "issued" || name === "accessed"
                                    ? "Provide Year only."
                                    : ""
                                }
                              />
                            ))}
                          </>
                        }
                      />
                    </>
                  )}
                </>
              }
              manualContent={
                <>
                  <AuthorForm authors={authors} setAuthors={setAuthors} />
                  <CitationTabs
                    citationTab={citationTab}
                    handleCitationTabChange={handleCitationTabChange}
                    citationInputs={
                      <>
                        {citationInputs?.map(({ name, isRequired }) => (
                          <TextField
                            key={name}
                            id={name}
                            name={name}
                            label={name.charAt(0).toUpperCase() + name.slice(1)}
                            value={
                              name === "issued"
                                ? citationData["issued"]?.[
                                    "date-parts"
                                  ]?.[0]?.[0] || ""
                                : name === "accessed"
                                ? citationData["accessed"]?.[
                                    "date-parts"
                                  ]?.[0]?.[0] || ""
                                : citationData[name] || ""
                            }
                            onChange={handleFieldChange}
                            fullWidth
                            required={isRequired}
                            helperText={
                              name === "issued" || name === "accessed"
                                ? "Provide Year only."
                                : ""
                            }
                          />
                        ))}
                      </>
                    }
                  />
                </>
              }
            />
          ) : (
            <>
              <AuthorForm authors={authors} setAuthors={setAuthors} />
              <CitationTabs
                citationTab={citationTab}
                handleCitationTabChange={handleCitationTabChange}
                citationInputs={
                  <>
                    {citationInputs?.map(({ name, isRequired }) => (
                      <TextField
                        key={name}
                        id={name}
                        name={name}
                        label={name.charAt(0).toUpperCase() + name.slice(1)}
                        value={
                          name === "issued"
                            ? citationData["issued"]?.[
                                "date-parts"
                              ]?.[0]?.[0] || ""
                            : name === "accessed"
                            ? citationData["accessed"]?.[
                                "date-parts"
                              ]?.[0]?.[0] || ""
                            : citationData[name] || ""
                        }
                        onChange={handleFieldChange}
                        fullWidth
                        required={isRequired}
                        helperText={
                          name === "issued" || name === "accessed"
                            ? "Provide Year only."
                            : ""
                        }
                      />
                    ))}
                  </>
                }
              />
            </>
          )}
        </Box>

        <Box
          item
          xs={12}
          md={3}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", md: "30%" },
            gap: 4,
          }}>
          <Box sx={previewBoxStyle}>
            {citationTab == "fullCitation" ? (
              <h3>{selectedStyle.toUpperCase()} Full Citation Preview</h3>
            ) : (
              <h3>In-Text Citation Preview</h3>
            )}

            <div>
              {(citationData &&
                (Object.keys(citationData).some((key) => {
                  if (key === "type") {
                    return;
                  } else if (key === "author") {
                    // Check if "given" or "family" has values
                    const author = citationData[key][0]; // Assuming there's only one author for simplicity
                    return author.given !== "" || author.family !== "";
                  } else if (key === "accessed" || key === "issued") {
                    // Check if there is a value inside
                    return citationData[key]["date-parts"][0][0]?.length > 0;
                  } else {
                    // For other keys, check if they have values normally
                    return (
                      citationData[key] !== undefined &&
                      citationData[key] !== null &&
                      citationData[key] !== ""
                    );
                  }
                }) ? (
                  <Box style={{ wordBreak: "break-word" }}>
                    {generatedCitation}
                    <Button
                      onClick={() => {
                        setCitations((prevCitations) => [
                          ...prevCitations,
                          generatedCitation,
                        ]);
                        resetCitationDataValues(
                          setGeneratedCitation,
                          setAuthors,
                          citationData,
                          setCitationData
                        );
                        setSelectedBook(null);
                      }}>
                      Save
                    </Button>
                  </Box>
                ) : null)) || ( // Render nothing if there are no values or if only "type" key exists
                <div>
                  Add your citation information, and we'll format it here.
                </div>
              )}
            </div>
          </Box>

          {citations.length > 0 && (
            <Box sx={previewBoxStyle}>
              <h3>Saved Citations</h3>
              {citations.map((citation, index) => (
                <Box key={index} style={{ wordBreak: "break-word" }}>
                  {generatedCitation}
                  <IconButton onClick={() => copyToClipboard(citation)}>
                    <CopyAllOutlined />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}

export default Citation;

/*
   // check if citationData has the required values
    if (
      inputConfig[selectedStyle][selectedSourceType].required.every(
        (field) => citationData[field]
      )
    ) {

 */
